interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
}

export type IconName =
  | 'sos'
  | 'lora'
  | 'gateway'
  | 'cloud'
  | 'responder'
  | 'map'
  | 'shield'
  | 'battery'
  | 'signal'
  | 'alert'
  | 'check'
  | 'arrow-right'
  | 'play'
  | 'wifi-off'
  | 'mountain'
  | 'satellite'
  | 'sun'
  | 'tree'
  | 'broadcast'
  | 'phone-off'
  | 'cpu'
  | 'radio'
  | 'pin'
  | 'heart'
  | 'users'
  | 'spark'
  | 'layers'
  | 'lock'
  | 'compass'
  | 'clock'
  | 'flame'
  | 'droplet'
  | 'wind'
  | 'globe'
  | 'box'
  | 'zap'
  | 'lightning'
  | 'flood'
  | 'earthquake'
  | 'fire'
  | 'arrow-up-right'
  | 'close'
  | 'team'
  | 'hospital'
  | 'helicopter'
  | 'route'
  | 'mesh'
  | 'sensor'
  | 'leaf'
  | 'eye'
  | 'wave'
  | 'message'
  | 'building'
  | 'menu';

export default function Icon({ name, size = 24, className, strokeWidth = 1.6 }: IconProps) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    'aria-hidden': true,
  };

  switch (name) {
    case 'sos':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M8.5 9.5 h7 M8.5 12 h7 M8.5 14.5 h7" />
        </svg>
      );
    case 'lora':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="2" />
          <path d="M8 12 a4 4 0 0 1 8 0" />
          <path d="M5 12 a7 7 0 0 1 14 0" />
          <path d="M2 12 a10 10 0 0 1 20 0" />
        </svg>
      );
    case 'gateway':
      return (
        <svg {...common}>
          <rect x="3" y="6" width="18" height="12" rx="2" />
          <path d="M7 10 v4 M12 10 v4 M17 10 v4" />
          <path d="M3 18 v2 a1 1 0 0 0 1 1 h16 a1 1 0 0 0 1 -1 v-2" />
        </svg>
      );
    case 'cloud':
      return (
        <svg {...common}>
          <path d="M7 18 a5 5 0 0 1 0 -10 a6 6 0 0 1 11 1 a4 4 0 0 1 0 9 z" />
        </svg>
      );
    case 'responder':
      return (
        <svg {...common}>
          <circle cx="12" cy="7" r="3.5" />
          <path d="M5 21 v-1 a5 5 0 0 1 5 -5 h4 a5 5 0 0 1 5 5 v1" />
        </svg>
      );
    case 'map':
      return (
        <svg {...common}>
          <path d="M9 3 L3 5 v16 l6 -2 6 2 6 -2 V3 l-6 2 z" />
          <path d="M9 3 v16 M15 5 v16" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...common}>
          <path d="M12 3 l8 3 v5 a9 9 0 0 1 -8 10 a9 9 0 0 1 -8 -10 V6 z" />
          <path d="M9 12 l2 2 4 -4" />
        </svg>
      );
    case 'battery':
      return (
        <svg {...common}>
          <rect x="3" y="7" width="16" height="10" rx="2" />
          <path d="M21 10 v4" />
          <path d="M6 10 v4 M9 10 v4 M12 10 v4" />
        </svg>
      );
    case 'signal':
      return (
        <svg {...common}>
          <path d="M4 20 h2 v-3 H4 z M9 20 h2 v-6 H9 z M14 20 h2 v-9 h-2 z M19 20 h2 v-12 h-2 z" />
        </svg>
      );
    case 'alert':
      return (
        <svg {...common}>
          <path d="M12 3 l10 18 H2 z" />
          <path d="M12 10 v5 M12 18 v0.1" />
        </svg>
      );
    case 'check':
      return (
        <svg {...common}>
          <path d="M4 12 l5 5 11 -11" />
        </svg>
      );
    case 'arrow-right':
      return (
        <svg {...common}>
          <path d="M5 12 h14 M13 5 l7 7 -7 7" />
        </svg>
      );
    case 'play':
      return (
        <svg {...common} fill="currentColor" stroke="none">
          <path d="M7 5 v14 l12 -7 z" />
        </svg>
      );
    case 'wifi-off':
      return (
        <svg {...common}>
          <path d="M3 3 l18 18" />
          <path d="M8.5 16.5 a5 5 0 0 1 7 0" />
          <path d="M5 13 a9 9 0 0 1 4 -2" />
          <path d="M19 13 a9 9 0 0 0 -6 -3" />
          <path d="M2 9 a14 14 0 0 1 5 -3" />
          <path d="M22 9 a14 14 0 0 0 -5 -3" />
        </svg>
      );
    case 'mountain':
      return (
        <svg {...common}>
          <path d="M3 20 l6 -10 4 6 3 -4 5 8 z" />
          <circle cx="9" cy="6" r="1" />
          <circle cx="14" cy="4" r="0.8" />
        </svg>
      );
    case 'satellite':
      return (
        <svg {...common}>
          <path d="M5 19 l-2 2 4 -1 1 -2 -2 -2 z" />
          <path d="M9 15 l5 -5 a4 4 0 0 1 5 5 l-5 5 a4 4 0 0 1 -5 -5 z" />
          <path d="M14 10 l3 3" />
        </svg>
      );
    case 'sun':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2 v2 M12 20 v2 M2 12 h2 M20 12 h2 M5 5 l1.5 1.5 M17.5 17.5 L19 19 M5 19 l1.5 -1.5 M17.5 6.5 L19 5" />
        </svg>
      );
    case 'tree':
      return (
        <svg {...common}>
          <path d="M12 3 l4 6 h-2 l3 5 h-2 l3 5 H6 l3 -5 H7 l3 -5 H8 z" />
          <path d="M12 19 v3" />
        </svg>
      );
    case 'broadcast':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="2" />
          <path d="M7 12 a5 5 0 0 1 5 -5" />
          <path d="M12 17 a5 5 0 0 0 5 -5" />
          <path d="M3 12 a9 9 0 0 1 9 -9" />
          <path d="M12 21 a9 9 0 0 0 9 -9" />
        </svg>
      );
    case 'phone-off':
      return (
        <svg {...common}>
          <path d="M3 3 l18 18" />
          <path d="M5 5 a14 14 0 0 0 14 14 l2 -2 -3 -3 -2 1 a10 10 0 0 1 -7 -7 l1 -2 -3 -3 z" />
        </svg>
      );
    case 'cpu':
      return (
        <svg {...common}>
          <rect x="6" y="6" width="12" height="12" rx="2" />
          <rect x="9" y="9" width="6" height="6" />
          <path d="M3 9 h3 M3 12 h3 M3 15 h3 M9 3 v3 M12 3 v3 M15 3 v3 M9 18 v3 M12 18 v3 M15 18 v3 M18 9 h3 M18 12 h3 M18 15 h3" />
        </svg>
      );
    case 'radio':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="2" />
          <path d="M5.5 5.5 a9 9 0 0 1 13 0" />
          <path d="M18.5 18.5 a9 9 0 0 0 0 -13" />
          <path d="M8.5 8.5 a5 5 0 0 1 7 0" />
          <path d="M15.5 15.5 a5 5 0 0 0 0 -7" />
        </svg>
      );
    case 'pin':
      return (
        <svg {...common}>
          <path d="M12 22 s7 -7 7 -12 a7 7 0 1 0 -14 0 c0 5 7 12 7 12 z" />
          <circle cx="12" cy="10" r="2.5" />
        </svg>
      );
    case 'heart':
      return (
        <svg {...common}>
          <path d="M12 21 s-7 -4.5 -7 -10 a4.5 4.5 0 0 1 7 -3.5 a4.5 4.5 0 0 1 7 3.5 c0 5.5 -7 10 -7 10 z" />
        </svg>
      );
    case 'users':
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3" />
          <circle cx="17" cy="9" r="2.5" />
          <path d="M3 20 a6 6 0 0 1 12 0" />
          <path d="M14 20 a5 5 0 0 1 7 0" />
        </svg>
      );
    case 'spark':
      return (
        <svg {...common}>
          <path d="M12 3 v6 M12 15 v6 M3 12 h6 M15 12 h6 M5 5 l4 4 M15 15 l4 4 M5 19 l4 -4 M15 9 l4 -4" />
        </svg>
      );
    case 'layers':
      return (
        <svg {...common}>
          <path d="M12 3 l9 5 -9 5 -9 -5 z" />
          <path d="M3 12 l9 5 9 -5" />
          <path d="M3 17 l9 5 9 -5" />
        </svg>
      );
    case 'lock':
      return (
        <svg {...common}>
          <rect x="4" y="11" width="16" height="10" rx="2" />
          <path d="M8 11 V7 a4 4 0 0 1 8 0 v4" />
        </svg>
      );
    case 'compass':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M15 9 l-2 5 -5 2 2 -5 z" />
        </svg>
      );
    case 'clock':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7 v5 l3 2" />
        </svg>
      );
    case 'flame':
      return (
        <svg {...common}>
          <path d="M12 22 c4 0 7 -3 7 -7 0 -3 -2 -5 -3 -7 -1 2 -2 3 -3 3 0 -2 -1 -5 -1 -7 -2 3 -7 6 -7 11 0 4 3 7 7 7 z" />
        </svg>
      );
    case 'droplet':
      return (
        <svg {...common}>
          <path d="M12 3 s7 7 7 12 a7 7 0 1 1 -14 0 c0 -5 7 -12 7 -12 z" />
        </svg>
      );
    case 'wind':
      return (
        <svg {...common}>
          <path d="M3 8 h12 a3 3 0 1 0 -3 -3" />
          <path d="M3 16 h16 a3 3 0 1 1 -3 3" />
          <path d="M3 12 h9" />
        </svg>
      );
    case 'globe':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12 h18" />
          <path d="M12 3 a14 14 0 0 1 0 18 a14 14 0 0 1 0 -18" />
        </svg>
      );
    case 'box':
      return (
        <svg {...common}>
          <path d="M3 7 l9 -4 9 4 v10 l-9 4 -9 -4 z" />
          <path d="M3 7 l9 4 9 -4" />
          <path d="M12 11 v10" />
        </svg>
      );
    case 'zap':
      return (
        <svg {...common}>
          <path d="M13 2 L4 14 h7 l-1 8 9 -12 h-7 z" />
        </svg>
      );
    case 'lightning':
      return (<svg {...common}><path d="M13 2 L4 14 L11 14 L9 22 L20 10 L13 10 Z" /></svg>);
    case 'flood':
      return (<svg {...common}><path d="M2 14 Q5 11 7 14 T12 14 T17 14 T22 14 M2 18 Q5 15 7 18 T12 18 T17 18 T22 18 M2 10 Q5 7 7 10 T12 10 T17 10 T22 10" /></svg>);
    case 'earthquake':
      return (<svg {...common}><path d="M3 12 L6 12 L8 8 L10 16 L12 6 L14 18 L16 10 L18 12 L21 12" /></svg>);
    case 'fire':
      return (<svg {...common}><path d="M12 3 Q14 8 17 10 Q17 16 12 21 Q7 16 7 10 Q10 8 12 3 Z" /></svg>);
    case 'arrow-up-right':
      return (<svg {...common}><path d="M7 7 L17 7 M7 7 L7 17 M17 7 L7 7" /><path d="M17 7 L17 17" /></svg>);
    case 'close':
      return (<svg {...common}><path d="M6 6 L18 18 M18 6 L6 18" /></svg>);
    case 'team':
      return (<svg {...common}><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20 Q3 14 9 14 Q15 14 15 20 M14 20 Q14 16 17 16 Q21 16 21 20" /></svg>);
    case 'hospital':
      return (<svg {...common}><path d="M3 21 L21 21 M5 21 L5 9 L12 4 L19 9 L19 21 M10 13 L10 21 M14 13 L14 21" /><path d="M10 13 L14 13 M12 11 L12 15" /></svg>);
    case 'helicopter':
      return (<svg {...common}><path d="M2 6 L22 6 M11 6 L11 11 M3 11 L3 17 L21 17 L21 11 M4 14 L4 17 M18 14 L18 17" /></svg>);
    case 'route':
      return (<svg {...common}><circle cx="6" cy="6" r="2" /><circle cx="18" cy="18" r="2" /><path d="M6 8 L6 14 Q6 18 10 18 L14 18 Q18 18 18 16 L18 10 Q18 6 14 6 L10 6" /></svg>);
    case 'mesh':
      return (<svg {...common}><circle cx="5" cy="5" r="2" /><circle cx="19" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="5" cy="19" r="2" /><circle cx="19" cy="19" r="2" /><path d="M6.5 6.5 L10.5 10.5 M17.5 6.5 L13.5 10.5 M6.5 17.5 L10.5 13.5 M17.5 17.5 L13.5 13.5 M12 14 L12 17" /></svg>);
    case 'sensor':
      return (<svg {...common}><circle cx="12" cy="12" r="3" /><path d="M5 12 Q5 5 12 5 Q19 5 19 12" /><path d="M8 12 Q8 8 12 8 Q16 8 16 12" /><path d="M12 15 L12 21" /></svg>);
    case 'leaf':
      return (<svg {...common}><path d="M5 19 Q5 7 19 5 Q17 19 5 19 Z" /><path d="M5 19 L13 11" /></svg>);
    case 'eye':
      return (<svg {...common}><path d="M2 12 Q6 5 12 5 Q18 5 22 12 Q18 19 12 19 Q6 19 2 12 Z" /><circle cx="12" cy="12" r="3" /></svg>);
    case 'wave':
      return (<svg {...common}><path d="M2 12 Q5 7 8 12 T14 12 T20 12" /><path d="M2 17 Q5 12 8 17 T14 17 T20 17" /><path d="M2 7 Q5 2 8 7 T14 7 T20 7" /></svg>);
    case 'message':
      return (<svg {...common}><path d="M4 5 L20 5 Q21 5 21 6 L21 16 Q21 17 20 17 L9 17 L4 21 Z" /></svg>);
    case 'building':
      return (<svg {...common}><path d="M3 21 L21 21 M5 21 L5 7 L13 7 L13 21 M13 11 L19 11 L19 21 M8 10 L8 12 M8 14 L8 16 M8 17 L8 18" /></svg>);
    case 'sun':
      return (<svg {...common}><circle cx="12" cy="12" r="4" /><path d="M12 3 L12 5 M12 19 L12 21 M3 12 L5 12 M19 12 L21 12 M5.5 5.5 L7 7 M17 17 L18.5 18.5 M5.5 18.5 L7 17 M17 7 L18.5 5.5" /></svg>);
    case 'box':
      return (<svg {...common}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 7 L21 7 M3 17 L21 17" /></svg>);
    case 'zap':
      return (<svg {...common}><path d="M13 2 L4 14 L11 14 L9 22 L20 10 L13 10 Z" /></svg>);
    case 'wind':
      return (<svg {...common}><path d="M2 12 Q6 7 10 12 T18 12 M2 17 Q6 12 10 17 T18 17" /></svg>);
    case 'droplet':
      return (<svg {...common}><path d="M12 2 Q16 12 12 22 Q8 12 12 2" /><path d="M12 13 L12 18" /></svg>);
    case 'flame':
      return (<svg {...common}><path d="M12 2 Q16 8 18 12 Q12 22 6 12 Q8 8 12 2" /></svg>);
    case 'globe':
      return (<svg {...common}><circle cx="12" cy="12" r="9" /><path d="M3 12 L21 12" /><path d="M12 3 Q16 12 12 21 Q8 12 12 3" /></svg>);
    case 'menu':
      return (
        <svg {...common}>
          <path d="M3 6 h18 M3 12 h18 M3 18 h18" />
        </svg>
      );
    default: {
      const _exhaustive: never = name;
      return null;
    }
  }
}
